import { IconNameV1 } from 'common/icon/iconV1.js';
import { LatLngTuple } from 'leaflet';
import { WarningsType } from 'types/markerTypes/location-markers-type.js';
import { ILink } from 'types/navigation.js';
import { WarningSeverity } from 'utilities/weather-warnings/warning-severity.js';
import { WarningEventTypeLandHybrid, WarningEventTypeMarineHybrid } from './types-events.js';
import { RegionsLand, RegionsMarineCoastal, RegionsMarineRecreational } from './types-regions.js';

/** Be careful renaming these as regular expression targets  `WEATHER_WARNINGS:LOCATION:GET` */
export enum TypeKeys {
  // WEATHER_WARNINGS_FETCH_INIT = 'WEATHER_WARNINGS_FETCH_INIT',
  WEATHER_WARNINGS_FETCH_RETRY = 'WEATHER_WARNINGS_FETCH_RETRY',
  WEATHER_WARNINGS_FETCH_EXPIRED = 'WEATHER_WARNINGS_FETCH_EXPIRED',
  // WEATHER_WARNINGS_FETCH_SUCCESS = 'WEATHER_WARNINGS_FETCH_SUCCESS',
  // WEATHER_WARNINGS_FETCH_ERROR = 'WEATHER_WARNINGS_FETCH_ERROR',
  // WEATHER_WARNINGS_CLEAR = 'WEATHER_WARNINGS_CLEAR',
}
export const WARNINGS_EXPIRY_PERIOD = 180000; // 3 minutes
export const WARNINGS_NOT_AVAILABLE_MESSAGE = 'Unable to access latest warnings, please refresh the page.';
export const WARNINGS_COUNT_ERROR = 'error';

/** By default uses enum vals allows type option to use 'json' to check types */
export type IWarningCore<T extends 'JS' | 'JSON' = 'JS'> = {
  /** @see http://wiki.met.co.nz/display/PWM/metservice.com+-+warnings+service+API+endpoints */
  name: string;
  warnLevel: T extends 'JS' ? WarningSeverity : `${WarningSeverity}`;
  icon: IconNameV1;
  displayRegions: string[];
  issuedAt: string;
  nextIssueAt: T extends 'JS' ? string : string | null;
  threatStartTime?: T extends 'JS' ? string : string | null;
  threatEndTime?: T extends 'JS' ? string : string | null;
  areaDescription?: string;
  situation?: { headline: string; statement: string } | null;
  threatPeriod?: string;
  url?: string;
  // Used for warnings that need to be rendered on the map
  polygon?: LatLngTuple[];
  polygons?: LatLngTuple[][] | LatLngTuple[][][] | null;
  impact?: string | null; // Civil defence warning
  /** iceLocation is used to identify swell warnings on warnings table */
  iceLocation?: string;
  key?: string;
  /** Used for filtering - The category of warning, EG: snow, wind, thunderstorm, rain, etc */
  text: T extends 'JS' ? string : string | null;
  areaShortName?: string;
  // This causes a warning to only show in the banner, not in the modules on the page
  onlyShowInBanner?: boolean;
  // This field is used by the `WarningsTile`. If it's defined the component will
  // display all of the warnings that have that category set.
  category?: string;
  /** To support HTML content as part of the Warnings.*/
  content?: string;
  /** Used in orange warnings to identify any risk of increasing */
  instruction?: null | string;
  instructionAttribution?: null | string;
  // likelihoodOfEscalation?: null | string;
  /**
   * Additional properties not used
   * https://github.com/microsoft/TypeScript/issues/3755
   */
  [others: string]: unknown;
};

export type IWarning<T extends 'JS' | 'JSON' = 'JS'> = EventTypeContainer & IWarningCore<T>;

interface ILandWarningType {
  type: WarningsType.Land;
  subType?: undefined;
  /** Regions the warning applies to */
  regions?: `${RegionsLand}`[];
  // unique identifier
  capId: string;
  eventType: `${WarningEventTypeLandHybrid}`;
}

interface IMarineCoastalType {
  type: WarningsType.Marine;
  subType: 'coastal';
  /** Regions the warning applies to */
  regions?: `${RegionsMarineCoastal}`[];
  eventType: `${WarningEventTypeMarineHybrid}`;
}

interface IMarineRecreationalType {
  type: WarningsType.Marine;
  subType: 'recreational';
  /** Regions the warning applies to */
  regions?: `${RegionsMarineRecreational}`[];
  eventType: `${WarningEventTypeMarineHybrid}`;
}

interface IMarineHighSeasType {
  type: 'high-seas';
  subType?: undefined;
  /** Regions the warning applies to */
  regions?: `${RegionsMarineRecreational}`[];
  eventType: `${WarningEventTypeMarineHybrid}`;
}

type EventTypeContainer = ILandWarningType | IMarineCoastalType | IMarineRecreationalType | IMarineHighSeasType;

export interface IWarningSummary<T extends 'JS' | 'JSON' = 'JS'> extends ILink {
  icon?: IconNameV1;
  warnLevel: T extends 'JS' ? WarningSeverity : `${WarningSeverity}`;
}

export enum WarningLoadingState {
  loading = 'loading',
  loaded = 'loaded',
  failed = 'failed',
}
export interface ILocationWarningType {
  type: string;
  key: string;
  hideDate?: boolean;
}

/**
 * JSON received from weather warnings-service
 * By default uses enum vals allows type option to use 'json' to check types
 */
export interface IWarningPayloadData<T extends 'JS' | 'JSON' = 'JS'> {
  /** @see http://wiki.met.co.nz/display/PWM/metservice.com+-+warnings+service+API+endpoints */
  // locations?: IWarningLocation[];
  /** Used to display warning text in warning tiles on warning page */
  contentPerCategory?: {
    [category: string]: {
      content: string;
    };
  };
  /** used to display warnings summary markup on warnings panel  */
  summary?: IWarningSummary<T>[];
  warnings?: IWarning<T>[];
  warningAreaName?: string;
  warningPageUrl?: string;
  // hasActiveWarning?: boolean;
  // highestWarnLevel?: 'red' | 'orange' | 'yellow';
  /**
   * Additional properties not used
   * https://github.com/microsoft/TypeScript/issues/3755
   */
  [others: string]: unknown;
}

/** State adds loadState within redux to original payload */
export interface ILocationWarningState<T extends 'JS' | 'JSON' = 'JS'> extends IWarningPayloadData<T> {
  loadState: T extends 'JSON' ? `${WarningLoadingState}` : WarningLoadingState;
}

/** Redux weather warnings state */
export interface IWeatherWarningsState<T extends 'JS' | 'JSON' = 'JS'> {
  warnings: {
    [locationTypeKey: string]: ILocationWarningState<T>;
  };
}

/** Raw action with successful payload and request params */
export interface IWarningPayload {
  locationRequest: ILocationWarningType;
  data: IWarningPayloadData;
}
